/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1901789192 — ServiceRobot */

import {
    GoogleReCaptchaCheckbox,
    GoogleReCaptchaProvider,
} from '@google-recaptcha/react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { ContentBox, PrimaryButton, ScreenTitle, ScreenWrapper } from '../../../../shared/ui';
import { AnimatedIcon } from '../../../../shared/ui/AnimatedIcon/AnimatedIcon';
import robotAnimation from '../../../../shared/ui/AnimatedIcon/animations/robot.json';
import { PUBLIC_CAPTCHA_KEY } from '../../../../shared/env.config';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { useGetFiscData } from '../../hooks/useGetFiscData';
import { useServiceStore } from '../../hooks/useServiceStore';
import { SERVICE_ROUTES } from '../../service.constants';
import { ReceiptData } from '../../service.types';
import { isReceiptValid } from '../../service.utils';

import styles from './ServiceRobot.module.css';

export const ServiceRobot = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const vm = useAppStore((s) => s.vm);
    const setReceiptData = useServiceStore((s) => s.setReceiptData);
    
    const getFiscDataMutation = useGetFiscData();

    const navigateToMainPage = (): void => {
        navigate(generatePath(SERVICE_ROUTES.main, { id: vm.qr }), {
            replace: true,
        });
    };

    const onGetFiscDataSuccess = (receipt: ReceiptData) => {
        setReceiptData(receipt);

        const route = isReceiptValid(receipt)
            ? SERVICE_ROUTES.complete
            : SERVICE_ROUTES.wait;
            
        navigate(generatePath(route, { id: vm.qr }), { replace: true });
    };

    const onGetFiscDataError = (error: Error) => {
        console.error(
            { module: 'service', action: 'getFiscData', vmId: vm.vm_id },
            error,
        );
        navigate(generatePath(SERVICE_ROUTES.fail, { id: vm.qr }), {
            replace: true,
        });
    };

    const onCaptchaVerify = (_token: string) => {
        getFiscDataMutation.mutate(vm.vm_id, {
            onSuccess: onGetFiscDataSuccess,
            onError: onGetFiscDataError,
        });
    };

    return (
        <ScreenWrapper>
            <ScreenTitle>{t('Robot')} </ScreenTitle>
            <ContentBox>
                <AnimatedIcon
                    animationData={robotAnimation}
                    className={styles.canvasObject}
                />

                <GoogleReCaptchaProvider
                    type="v2-checkbox"
                    siteKey={PUBLIC_CAPTCHA_KEY}
                    language={vm.language_code}
                >
                    <GoogleReCaptchaCheckbox
                        size="normal"
                        onChange={onCaptchaVerify}
                    />
                </GoogleReCaptchaProvider>
            </ContentBox>
            <PrimaryButton onClick={navigateToMainPage}>
                {t('Main page')}
            </PrimaryButton>
        </ScreenWrapper>
    );
};
